import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { GroupeBien } from '../models/models'; // Ensure to define GroupeBien model
import api from './api';

interface GroupeBienState {
  groupeBienData: GroupeBien[];
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedGroupeBien: GroupeBien | null;
}

const initialState: GroupeBienState = {
  groupeBienData: [],
  total: 0,
  totalPages: 0,
  currentPage: 0,
  loading: false,
  error: null,
  successMessage: null,
  selectedGroupeBien: null,
};

// Fetch all GroupeBien with pagination
export const fetchGroupeBienData = createAsyncThunk<
  { data: GroupeBien[]; total: number; totalPages: number; currentPage: number },
  { limit: number; offset: number },
  { rejectValue: string }
>(
  'groupeBien/fetchGroupeBienData',
  async ({ limit, offset }, thunkAPI) => {
    try {
      const response = await api.get('/groupebiens/getAll', { params: { limit, offset } });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to load data.');
    }
  }
);

export const fetchGroupeBienDataByName = createAsyncThunk<
  { data: GroupeBien[]; total: number; totalPages: number; currentPage: number },
  { nom:string,limit: number; offset: number },
  { rejectValue: string }
>(
  'groupeBien/fetchGroupeBienDataByName',
  async ({nom, limit, offset }, thunkAPI) => {
    try {
      const response = await api.get('/groupebiens/getByName', { params: {nom, limit, offset } });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to load data.');
    }
  }
);

// Create or update a GroupeBien
export const createGroupeBien = createAsyncThunk<
  GroupeBien, // Returns the created/updated GroupeBien
  GroupeBien, // Parameters for creating or updating
  { rejectValue: string }
>(
  'groupeBien/createOrUpdateGroupeBien',
  async (groupeBienData, thunkAPI) => {
    try {
      const response = await api.post('/groupebiens/createOrUpdate', groupeBienData);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to create or update.');
    }
  }
);

// Delete a GroupeBien
export const deleteGroupeBien = createAsyncThunk<
  number, // Returns the ID of the deleted GroupeBien
  number, // ID of the GroupeBien to delete
  { rejectValue: string }
>(
  'groupeBien/deleteGroupeBien',
  async (groupeBienId, thunkAPI) => {
    try {
      await api.delete(`/groupebiens`, { params: { id: groupeBienId } });
      return groupeBienId;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to delete.');
    }
  }
);

const groupeBienSlice = createSlice({
  name: 'groupeBien',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setSelectedGroupeBien: (state, action: PayloadAction<GroupeBien | null>) => {
      state.selectedGroupeBien = action.payload;
    },
    clearGroupBienData:(state)=>{
      state.groupeBienData=[];
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch GroupeBien data
      .addCase(fetchGroupeBienData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupeBienData.fulfilled, (state, action) => {
        state.loading = false;
        state.groupeBienData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchGroupeBienData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchGroupeBienDataByName.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupeBienDataByName.fulfilled, (state, action) => {
        state.loading = false;
        state.groupeBienData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchGroupeBienDataByName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create or update GroupeBien
      .addCase(createGroupeBien.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createGroupeBien.fulfilled, (state, action: PayloadAction<GroupeBien>) => {
        state.loading = false;
        state.successMessage = 'Creation/Update successful!';
        // const index = state.data.findIndex((g) => g.id === action.payload.id);
        // if (index !== -1) {
        //   state.data[index] = action.payload; // Update existing item
        // } else {
        //   state.data.unshift(action.payload); // Add new item if not found
        // }
      })
      .addCase(createGroupeBien.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete GroupeBien
      .addCase(deleteGroupeBien.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteGroupeBien.fulfilled, (state, action: PayloadAction<number>) => {
        state.loading = false;
        state.successMessage = 'Deletion successful!';
        // state.data = state.data.filter((g) => g.id !== action.payload);
        // if (state.selectedGroupeBien?.id === action.payload) {
        //   state.selectedGroupeBien = null;
        // }
      })
      .addCase(deleteGroupeBien.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setCurrentPage, setSelectedGroupeBien, clearMessages,clearGroupBienData } = groupeBienSlice.actions;
export default groupeBienSlice.reducer;
